import { SupportingItemsPorts } from '../../../../ports'
import { action, makeObservable, computed, observable, override } from 'mobx'
import ModalViewModel from '../../../../../infra/ModalViewModel'
import {
  formatSupportingItemSpec,
  InventoryItemSupportingItem,
  InventoryItemSupportingItemSpec,
  InventoryItemSupportingItemSpecTypes,
} from '../../../InventoryItem'
import DocumentType from '../../../../../documents/DocumentType'
import { UploaderUserType } from '../../../../../documents/dtos/document-type'

export default class ManageSupportingItemsViewModel extends ModalViewModel {
  @observable
  supportingItemFromDocumentTypesViewModels: SupportingItemViewModel[] = []

  @observable
  supportingItemFromInputsViewModels: SupportingItemViewModel[] = []

  constructor(
    private adapter: SupportingItemsPorts,
    private getSupportingItems: () => InventoryItemSupportingItem[],
    private setSupportingItems: (items: InventoryItemSupportingItem[]) => void,
  ) {
    super()
    makeObservable(this)
  }

  activate() {
    this.configureSupportingItemOptions(this.documentTypes)
  }

  @computed
  get inputSupportingItems() {
    return [
      {
        type: InventoryItemSupportingItemSpecTypes.BUSINESS_INFO,
      },
      {
        type: InventoryItemSupportingItemSpecTypes.BUSINESS_REFUND_METHOD_INFO,
      },
      {
        type: InventoryItemSupportingItemSpecTypes.DEPENDENTS_INFO,
      },
      {
        type: InventoryItemSupportingItemSpecTypes.NOTES,
      },
      {
        type: InventoryItemSupportingItemSpecTypes.PERSONAL_INFO,
      },
      {
        type: InventoryItemSupportingItemSpecTypes.PERSONAL_REFUND_METHOD_INFO,
      },
      {
        type: InventoryItemSupportingItemSpecTypes.SPOUSAL_INFO,
      },
    ] as InventoryItemSupportingItemSpec[]
  }

  @computed
  get documentTypes() {
    return this.adapter.documentTypes
  }

  @computed
  get totalSelected() {
    return this.supportingItemFromDocumentTypesViewModels
      .concat(this.supportingItemFromInputsViewModels)
      .filter((item) => item.requested).length
  }

  @action.bound
  configureSupportingItemOptions(documentTypes: DocumentType[]) {
    const supportingItems = this.getSupportingItems()
    const clientDocTypes = documentTypes
      .filter((d) => d.uploaderUserType === UploaderUserType.Client)
      .sort((a, b) => a.name.localeCompare(b.name))

    this.supportingItemFromDocumentTypesViewModels = clientDocTypes.map(
      (documentType) => {
        const existing = supportingItems.find(
          (one) =>
            one.spec.type === InventoryItemSupportingItemSpecTypes.DOCUMENT &&
            one.spec.documentType?.shortId === documentType.shortId,
        )
        return new SupportingItemViewModel(!!existing, !!existing?.required, {
          type: InventoryItemSupportingItemSpecTypes.DOCUMENT,
          documentType,
        })
      },
    )

    this.supportingItemFromInputsViewModels = this.inputSupportingItems.map(
      (inputItem) => {
        const existing = supportingItems.find(
          (one) => one.spec.type === inputItem.type,
        )
        return new SupportingItemViewModel(
          !!existing,
          !!existing?.required,
          inputItem,
        )
      },
    )
  }

  @action.bound
  submit() {
    const supportingItems = this.supportingItemFromDocumentTypesViewModels
      .concat(this.supportingItemFromInputsViewModels)
      .filter((item) => item.requested)
      .map((item) => {
        return {
          spec: item.spec,
          required: item.required,
        }
      })
    this.setSupportingItems(supportingItems)
    this.close()
  }

  @override
  show() {
    this.activate()
    super.show()
  }
}

export class SupportingItemViewModel {
  requested: boolean = false
  required: boolean = false
  readonly spec: InventoryItemSupportingItemSpec

  constructor(
    requested: boolean,
    required: boolean,
    spec: InventoryItemSupportingItemSpec,
  ) {
    this.requested = requested
    this.required = required
    this.spec = spec
    makeObservable(this, {
      required: observable,
      requested: observable,
      toggleRequired: action.bound,
      toggleRequested: action.bound,
      name: computed,
    })
  }

  toggleRequired() {
    this.required = !this.required
  }

  toggleRequested() {
    this.requested = !this.requested
  }

  get name() {
    return formatSupportingItemSpec(this.spec)
  }
}
